import {
  closestCenter,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  DND_TOUCH_ACTIVATION,
  Icon,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  Text,
} from "@salestrak/ui";
import React, { useMemo, useState } from "react";

import { useAppData, type WorkspaceWorkflow } from "#app/lib/data";
import { usePermission, useViewerIsShareOnly } from "#app/lib/hooks/use-permission";
import { useWorkflowOrder } from "#app/lib/hooks/use-workflow-order";
import { useCloseSidebarIfCompact } from "#app/lib/mobile-sidebar-context";
import { useNavDialogActions } from "#app/lib/nav-dialogs-context";
import { useModuleRail } from "#app/components/navigation/module-rail/module-rail-context";
import { DeleteWorkflowDialog } from "#app/components/workflows/delete-workflow-dialog";
import { useWorkflowMenuActions } from "#app/components/workflows/workflow-menu-items";
import { SidebarSection } from "@salestrak/ui";
import { WorkflowItem } from "../workflow-item";
import { useSidebarNav } from "../use-sidebar-nav";

/**
 * "Workflows" module-menu section — a "Shared with me" row (the projects
 * other people handed the viewer, across every workflow: share-only viewers
 * only — Guest and Limited Member, whose work arrives that way; a
 * public-access role never gets the row), then the user's ordered workflow
 * list (drag-sortable on
 * desktop), then a "Create Workflow" row (gated on the `create.workflows`
 * permission). Shared by the Home and Workflows module menus, which differ
 * in one structural way:
 *
 * `grouped` (the default) wraps the rows in the titled, collapsible
 * `SidebarSection` — Home's presentation, where the list coexists with other
 * sections and needs a name. `grouped={false}` renders the rows flat, no
 * header and no collapse — the Workflows module's presentation, where the
 * pane title IS the group label (the reference module-menu grammar; the
 * pane header's "+" menu already carries the create affordance the section
 * header duplicated).
 */
export function MyWorkflowsSection({
  grouped = true,
}: {
  grouped?: boolean;
}) {
  const { navigate, isActive } = useSidebarNav();
  const { workflows, refetch: refetchAppData } = useAppData();
  const { sortedWorkflows, handleDragEnd } = useWorkflowOrder(workflows);
  // Sidebar scope toggles (the Workflows pane's title menu),
  // both off by default: archived workflows hide, and a privileged viewer's
  // list narrows to workflows their own grants reach. Filtering happens on
  // the RENDERED list only — the order hook keeps the full set so hidden
  // workflows hold their drag positions for when they're shown again.
  const { showAllWorkflows, showArchivedWorkflows } = useModuleRail();
  const visibleWorkflows = useMemo(
    () =>
      sortedWorkflows.filter(
        (wf) =>
          (showArchivedWorkflows || !wf.archived) &&
          (showAllWorkflows || wf.viewerHasExplicitAccess),
      ),
    [sortedWorkflows, showAllWorkflows, showArchivedWorkflows],
  );
  const visibleWorkflowIds = useMemo(
    () => visibleWorkflows.map((wf) => wf.workflowId),
    [visibleWorkflows],
  );
  const canCreateWorkflow = usePermission("create.workflows");
  // Share-only viewers only: their work arrives as hand-offs, so the row is
  // their front door. A public-access role reaches shared projects through
  // the workflows themselves and never gets the row.
  const showShared = useViewerIsShareOnly();
  // The row menus' shared handler set — wired ONCE here, never per row (the
  // mutation hooks live inside it).
  const menuActions = useWorkflowMenuActions();
  const closeDrawer = useCloseSidebarIfCompact();
  const nav = useNavDialogActions();
  const [deleteTarget, setDeleteTarget] = useState<WorkspaceWorkflow | null>(
    null,
  );

  // Capability-based, never viewport-gated: a mouse drags after 5px of
  // travel, a finger after a hold (a swipe before the hold ends scrolls the
  // pane) — the compact drawer reorders the same way the desktop pane does.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: DND_TOUCH_ACTIVATION }),
  );

  const openCreateWorkflow = () => {
    closeDrawer();
    nav.openCreateWorkflow();
  };

  // With no visible workflows, nothing shared AND no create permission
  // there's nothing to show or do — hide the whole section rather than render
  // an empty, actionless accordion. (No workflows + can create → keep it for
  // the CTA.)
  if (sortedWorkflows.length === 0 && !canCreateWorkflow && !showShared) {
    return null;
  }

  const rows = (
    <>
      {showShared ? (
        <SidebarMenuItem>
          <SidebarMenuButton
            isActive={isActive("/shared")}
            onClick={() => navigate("/shared")}
          >
            <Icon name="share" />
            <Text truncate>Shared with me</Text>
          </SidebarMenuButton>
        </SidebarMenuItem>
      ) : null}
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        sensors={sensors}
        // dnd-kit renders its screen-reader live region where the context
        // sits, and here that is inside the menu's list, where only list
        // items may go. The body keeps the list a list.
        accessibility={{ container: document.body }}
      >
        <SortableContext
          items={visibleWorkflowIds}
          strategy={verticalListSortingStrategy}
        >
          {visibleWorkflows.map((wf, index) => (
            <WorkflowItem
              isActive={isActive(`/workflow/${wf.workflowId}`)}
              key={`${wf.workflowId}-${index}`}
              onClick={() => navigate(`/workflow/${wf.workflowId}`)}
              menu={{
                actions: menuActions,
                // Backend-resolved share-management gate — the exact rule
                // the sharing mutations enforce (manage.users OR explicit
                // full_edit / creator / workflow-editor). Never derive this
                // from viewerAccessLevel: the public defaultAccessLevel
                // raises that level for everyone without granting share
                // management.
                canShare: wf.viewerCanManageSharing,
                onShare: () => {
                  closeDrawer();
                  nav.openWorkflowShare(wf.workflowId);
                },
                onRequestDelete: () => setDeleteTarget(wf),
                onRequestCreateWorkflow: openCreateWorkflow,
              }}
              sortable
              workflow={wf}
            />
          ))}
        </SortableContext>
      </DndContext>
      {canCreateWorkflow && (
        <SidebarMenuItem>
          <SidebarMenuButton onClick={openCreateWorkflow}>
            <Icon color="faint" name="plus" />
            <Text truncate variant="faint">
              Create Workflow
            </Text>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )}
    </>
  );

  return (
    <>
      {grouped ? (
        <SidebarSection
          // Header "+" — same flow as the Create Workflow row inside; omitted
          // entirely without create permission.
          action={
            canCreateWorkflow
              ? {
                  icon: "plus",
                  label: "Create Workflow",
                  onClick: openCreateWorkflow,
                }
              : undefined
          }
          title="Workflows"
        >
          {rows}
        </SidebarSection>
      ) : (
        <SidebarGroup>
          <SidebarMenu>{rows}</SidebarMenu>
        </SidebarGroup>
      )}
      <DeleteWorkflowDialog
        workflow={deleteTarget}
        open={deleteTarget != null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        // The sidebar list reads the workspace query, not the workflows
        // connection the mutation evicts from.
        onDeleted={() => refetchAppData()}
        onManageWorkflow={(id) => navigate(`/settings/workflows/${id}`)}
      />
    </>
  );
}
